import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Box, Button, Modal, Typography, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, TablePagination, Select, MenuItem, InputLabel, FormControl,
    InputAdornment, Popover,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

import Layout from './Layout';
import { useToast } from '../contexts/ToastContext';
import UsersService from '../services/UsersService';
import { useTranslation } from 'react-i18next';
import styles from './css/UsersPage.module.css';
import PageHeader from './PageHeader';

const underlineStyle = {
    '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
    '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

const generatePassword = () => Math.random().toString(36).slice(-10);

const SectionHeader = ({ number, label }) => (
    <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 2.5 }}>
        {number && (
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.25 }}>
                {number}
            </Typography>
        )}
        <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332', lineHeight: 1.2 }}>
            {label}
        </Typography>
    </Box>
);

const UsersPage = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9][0-9. ()-]{6,19}$/;

    const [errors, setErrors] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [totalNumberOfUsers, setTotalNumberOfUsers] = useState(null);
    const [users, setUsers] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const emptyUser = { username: '', password: '', role: '', firstName: '', lastName: '', phone: '', email: '' };
    const [newUser, setNewUser] = useState(emptyUser);

    const handleChange = (field, value) => setNewUser(prev => ({ ...prev, [field]: value }));

    const handleOpenModal = () => {
        setNewUser(emptyUser);
        setErrors({});
        setShowPassword(false);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setNewUser(emptyUser);
        setErrors({});
        setShowPassword(false);
    };

    const validateNewUser = (user) => {
        const e = {};
        if (!user.username.trim())                                 e.username  = t('usersPage.validation.usernameRequired');
        if (!user.password.trim())                                 e.password  = t('usersPage.validation.passwordRequired');
        if (!user.role)                                            e.role      = t('usersPage.validation.roleRequired');
        if (!user.email.trim() || !emailRegex.test(user.email))   e.email     = t('usersPage.validation.emailInvalid');
        if (!user.phone.trim() || !phoneRegex.test(user.phone))   e.phone     = t('usersPage.validation.phoneInvalid');
        if (!user.firstName.trim())                                e.firstName = t('usersPage.validation.firstNameRequired');
        if (!user.lastName.trim())                                 e.lastName  = t('usersPage.validation.lastNameRequired');
        return e;
    };

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const data = await UsersService.getUsers();
            setUsers(data.content);
            setTotalNumberOfUsers(data.totalElements);
        } catch (error) {
            showToast(t('usersPage.toasts.errorLoadingUsers'), 'warning');
            console.error('Failed to fetch users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddUser = async () => {
        const v = validateNewUser(newUser);
        setErrors(v);
        if (Object.keys(v).length > 0) {
            showToast(t('usersPage.toasts.fixHighlighted'), 'warning');
            return;
        }
        try {
            // Password is sent in plaintext and bcrypt-hashed server-side (H-7);
            // the backend no longer accepts client-computed hashes.
            const payload = { ...newUser };
            const response = await UsersService.createUser(payload);
            if (response) {
                setUsers(prev => [...prev, response]);
                handleCloseModal();
                showToast(t('usersPage.toasts.userCreated'), 'success');
            } else {
                showToast(t('usersPage.toasts.userCreateError'), 'error');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            showToast(t('usersPage.toasts.userCreateError'), 'error');
        }
    };

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <PageHeader
                    title={t('usersPage.title')}
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenModal}
                            sx={{ backgroundColor: '#427898', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                        >
                            {t('usersPage.addNewUser')}
                        </Button>
                    }
                />

                <TableContainer component={Paper} className={styles.tableWrap}>
                    <Table className={styles.tableMin}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('usersPage.table.username')}</TableCell>
                                <TableCell>{t('usersPage.table.role')}</TableCell>
                                <TableCell>{t('usersPage.table.firstName')}</TableCell>
                                <TableCell>{t('usersPage.table.lastName')}</TableCell>
                                <TableCell>{t('usersPage.table.phone')}</TableCell>
                                <TableCell>{t('usersPage.table.email')}</TableCell>
                                <TableCell>{t('usersPage.table.status')}</TableCell>
                                <TableCell>{t('usersPage.table.action')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((user) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>{user.username}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell>{user.firstName}</TableCell>
                                        <TableCell>{user.lastName}</TableCell>
                                        <TableCell>{user.phone}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.status}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedUser(user); }}>
                                                <MoreHorizIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={users.length}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </TableContainer>

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => { setAnchorEl(null); setSelectedUser(null); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={() => { if (selectedUser) navigate(`/users/${selectedUser.id}`); setAnchorEl(null); }}>
                        {t('common.edit')}
                    </MenuItem>
                </Popover>

                {/* ── Create User Modal ── */}
                <Modal open={isModalOpen} onClose={handleCloseModal}>
                    <Box className={styles.modalBox}>

                        {/* 4px brand accent bar */}
                        <Box sx={{ height: 4, backgroundColor: '#427898', flexShrink: 0 }} />

                        {/* Header */}
                        <Box className={styles.modalHeader}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <IconButton size="small" onClick={handleCloseModal} sx={{ color: '#64748b' }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332' }}>
                                    {t('usersPage.addNewUser')}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                onClick={handleAddUser}
                                sx={{ backgroundColor: '#427898', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                            >
                                {t('usersPage.form.createUser')}
                            </Button>
                        </Box>

                        {/* Body */}
                        <Box className={styles.modalBody}>

                            {/* ── Section 01: Account ── */}
                            <Box sx={{ mb: 4 }}>
                                <SectionHeader number="01" label={t('usersPage.modal.accountSection')} />

                                {/* Username */}
                                <TextField
                                    label={t('usersPage.form.username')}
                                    fullWidth
                                    variant="standard"
                                    sx={underlineStyle}
                                    margin="normal"
                                    value={newUser.username}
                                    onChange={(e) => handleChange('username', e.target.value)}
                                    error={!!errors.username}
                                    helperText={errors.username || ' '}
                                />

                                {/* Password */}
                                <TextField
                                    label={t('usersPage.form.password')}
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    variant="standard"
                                    sx={underlineStyle}
                                    margin="normal"
                                    value={newUser.password}
                                    onChange={(e) => handleChange('password', e.target.value)}
                                    error={!!errors.password}
                                    helperText={errors.password || ' '}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" edge="end" onClick={() => setShowPassword(p => !p)}
                                                    sx={{ color: '#94a3b8', '&:hover': { color: '#427898' } }}>
                                                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                {/* Password utilities */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -0.5, mb: 1 }}>
                                    <Button
                                        size="small"
                                        startIcon={<EnhancedEncryptionIcon sx={{ fontSize: '14px !important' }} />}
                                        onClick={() => handleChange('password', generatePassword())}
                                        sx={{ textTransform: 'none', color: '#427898', fontSize: 12, borderRadius: '6px', py: 0.4, px: 1, '&:hover': { backgroundColor: '#eef4f8' } }}
                                    >
                                        {t('usersPage.form.generatePassword')}
                                    </Button>
                                    <IconButton
                                        size="small"
                                        onClick={() => { navigator.clipboard.writeText(newUser.password || ''); showToast(t('usersPage.toasts.passwordCopied'), 'info'); }}
                                        sx={{ color: '#94a3b8', '&:hover': { color: '#427898', backgroundColor: '#eef4f8' } }}
                                    >
                                        <FileCopyIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>

                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 3, lineHeight: 1.5 }}>
                                    {t('usersPage.form.passwordHint')}
                                </Typography>

                                {/* Role + Email */}
                                <div className={styles.formRowGrid2}>
                                    <FormControl fullWidth variant="standard" sx={underlineStyle}>
                                        <InputLabel error={!!errors.role}>{t('usersPage.form.role')}</InputLabel>
                                        <Select
                                            value={newUser.role}
                                            onChange={(e) => handleChange('role', e.target.value)}
                                            error={!!errors.role}
                                        >
                                            <MenuItem value="ADMIN">Admin</MenuItem>
                                            <MenuItem value="EMPLOYEE">Employee</MenuItem>
                                            <MenuItem value="ACCOUNTANT">Accountant</MenuItem>
                                        </Select>
                                        <Typography variant="caption" sx={{ color: errors.role ? '#d32f2f' : 'transparent', mt: 0.5, minHeight: 18, display: 'block' }}>
                                            {errors.role || ' '}
                                        </Typography>
                                    </FormControl>

                                    <TextField
                                        label={t('usersPage.form.email')}
                                        fullWidth
                                        variant="standard"
                                        sx={underlineStyle}
                                        margin="normal"
                                        value={newUser.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        error={!!errors.email}
                                        helperText={errors.email || ' '}
                                    />
                                </div>
                            </Box>

                            {/* ── Section 02: Personal Details ── */}
                            <Box>
                                <SectionHeader number="02" label={t('usersPage.modal.personalSection')} />

                                {/* First + Last name */}
                                <div className={styles.formRowGrid2}>
                                    <TextField
                                        label={t('usersPage.form.firstName')}
                                        fullWidth
                                        variant="standard"
                                        sx={underlineStyle}
                                        margin="normal"
                                        value={newUser.firstName}
                                        onChange={(e) => handleChange('firstName', e.target.value)}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName || ' '}
                                    />
                                    <TextField
                                        label={t('usersPage.form.lastName')}
                                        fullWidth
                                        variant="standard"
                                        sx={underlineStyle}
                                        margin="normal"
                                        value={newUser.lastName}
                                        onChange={(e) => handleChange('lastName', e.target.value)}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName || ' '}
                                    />
                                </div>

                                {/* Phone */}
                                <TextField
                                    label={t('usersPage.form.phone')}
                                    fullWidth
                                    variant="standard"
                                    sx={underlineStyle}
                                    margin="normal"
                                    value={newUser.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    error={!!errors.phone}
                                    helperText={errors.phone || ' '}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </Layout>
    );
};

export default UsersPage;
